import React, { useState, useEffect, useRef, act } from "react";
import { Navigate } from "react-router-dom";
import Grid from "@mui/material/Grid2";

import { 
  Campaign, 
  CampaignChar, 
  Character,
} from "@dndModels";
import API from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";

import DandDButtonBox from "@dndComponents/DandDButtonBoxSimple";
import CharConnectUtilities from "@dndUtilities/char_connect_utilities";
import CampaignCharacterInput from "./CampaignCharacterInput";

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const connect_util = CharConnectUtilities.getInstance();

interface Props {
  campaign: Campaign;
}


export default function CampaignCharactersInput({ campaign }: Props) {
  const [reloading, setReloading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [my_characters, setMyCharacters] = useState<Character[]>([]);
  const [add_existing, setAddExisting] = useState(false);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);
    
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  if (navigateTo !== null) {
    return <Navigate to={navigateTo} />;
  } else if (add_existing) {
    return (
      <Grid container spacing={0} direction="column">
        <Grid>
          Adding an existing character which doesn"t comply with the allowed options for this campaign will automatically undo those options on the character.<br/>
          This may include race, subrace, class, subclass, gifts, items, and spells.
        </Grid>
        <Grid container spacing={0} direction="row">
          { my_characters.map(char => {
            return (
              <Grid size={4} key={char._id}
                container spacing={0} direction="row">
                <Grid size={12}>{ char.name === "" ? "Nameless" : char.name }</Grid>
                <Grid size={12}>
                  <DandDButtonBox name="Join" 
                    disabled={ processing || !api.online } 
                    onClick={ async () => {
                      setProcessing(true);
                      const c_char = new CampaignChar();
                      c_char.campaign_id = campaign._id;
                      c_char.campaign = campaign;
                      c_char.char_id = char._id;
                      c_char.char = char;
                      await api.upsertObject("campaign_char", c_char);
                      campaign.c_chars.push(c_char);
                      char.c_char = c_char;
                      campaign.characters.push(char);
                      setAddExisting(false);
                      setProcessing(false);
                    }}
                  />
                </Grid>
              </Grid>
            );
          })}
        </Grid>
        <Grid>
          <DandDButtonBox name="Cancel" 
            disabled={ processing }
            onClick={() => {
              setAddExisting(false);
            }}
          />
        </Grid>
      </Grid>
    );
  } else {
    return (
      <Grid container spacing={0} direction="row">
        { api.user && 
          <Grid size={12}
            container spacing={0} direction="row">
            <Grid size={4}>
              <DandDButtonBox name="Add Existing Character" 
                disabled={ processing || !api.online } 
                onClick={ async () => {
                  if (my_characters.length === 0 && api.user) {
                    if (api.user.my_characters.length === 0) {
                      setProcessing(true);
                      api.user.my_characters = await api.getPlayerObjects<Character>({ data_type: "character" });
                      setProcessing(false);
                    }
                    // debugger;
                    const campaign_char_ids = campaign.characters.map(o => o._id);
                    const active_chars = api.user.my_characters.filter(o => !o.is_archived && !campaign_char_ids.includes(o._id));
                    setMyCharacters(active_chars);
                  }
                  setAddExisting(true);
                }}
              />
            </Grid>
            <Grid size={2}>
              <DandDButtonBox name="Create Character" 
                disabled={ processing || !api.online } 
                onClick={() => {
                  if (api.user) {
                    setProcessing(true);
                    const new_char = new Character();
                    new_char.owner_id = api.user._id;
                    new_char.old_campaign_id = new_char._id;
                    connect_util.connectCampaign2(new_char, campaign);
                    new_char.last_campaign_update = campaign.last_updated;
                    char_util.enforceOptions(new_char, campaign).then(res => {
                      api.createObject("character", new_char).then(res => {
                        if (res && res.body.id) {
                          setProcessing(false);
                          setNavigateTo(`/character/${res.body.id}`);
                        } else {
                          console.log(res);
                        }
                      });
                    });
                  }
                }}
              />
            </Grid>
            <Grid size={2}>
              <DandDButtonBox name="Create Sidekick" 
                disabled={ processing || !api.online } 
                onClick={() => {
                  if (api.user) {
                    setProcessing(true);
                    const new_char = new Character();
                    new_char.owner_id = api.user._id;
                    new_char.old_campaign_id = new_char._id;
                    new_char.sidekick = true;
                    connect_util.connectCampaign2(new_char, campaign);
                    new_char.last_campaign_update = campaign.last_updated;
                    char_util.enforceOptions(new_char, campaign).then(res => {
                      api.createObject("character", new_char).then(res => {
                        if (res && res.body.id) {
                          setProcessing(false);
                          setNavigateTo(`/character/${res.body.id}`);
                        } else {
                          console.log(res);
                        }
                      });
                    });
                  }
                }}
              />
            </Grid>
            <Grid size={2}>
              <DandDButtonBox name="Create Unassigned Character" 
                disabled={ processing || !api.online } 
                onClick={() => {
                  setProcessing(true);
                  const new_char = new Character();
                  new_char.owner_id = "";
                  new_char.old_campaign_id = new_char._id;
                  connect_util.connectCampaign2(new_char, campaign);
                  new_char.last_campaign_update = campaign.last_updated;
                  char_util.enforceOptions(new_char, campaign).then(res => {
                    api.createObject("character", new_char).then(res => {
                      if (res && res.body.id) {
                        setProcessing(false);
                        setNavigateTo(`/character/${res.body.id}`);
                      } else {
                        console.log(res);
                      }
                    });
                  });
                }}
              />
            </Grid>
            <Grid size={2}>
              <DandDButtonBox name="Create Unassigned Sidekick" 
                disabled={ processing || !api.online } 
                onClick={() => {
                  setProcessing(true);
                  const new_char = new Character();
                  new_char.sidekick = true;
                  new_char.owner_id = "";
                  new_char.old_campaign_id = new_char._id;
                  connect_util.connectCampaign2(new_char, campaign);
                  new_char.last_campaign_update = campaign.last_updated;
                  char_util.enforceOptions(new_char, campaign).then(res => {
                    api.createObject("character", new_char).then(res => {
                      if (res && res.body.id) {
                        setProcessing(false);
                        setNavigateTo(`/character/${res.body.id}`);
                      } else {
                        console.log(res);
                      }
                    });
                  });
                }}
              />
            </Grid>
          </Grid>
        }
        { !reloading && campaign.c_chars.filter(o => o.active).length > 0 &&
          <>
            <Grid size={12} className="header centered">Active Characters</Grid>
            <Grid size={12}
              container spacing={0} direction="row">
              { campaign.c_chars.filter(o => o.active).map(c_char => {
                return (
                  <Grid size={4} key={c_char._id}>
                    <CampaignCharacterInput
                      c_char={c_char} 
                      campaign={campaign} 
                      // updateCampaign={() => { setReloading(true); }}
                      onUpdated={() => { setReloading(true); }}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </>
        }
        { !reloading && campaign.c_chars.filter(o => !o.active).length > 0 &&
          <>
            <Grid size={12} className="header centered">Inactive Characters</Grid>
            <Grid size={12}
              container spacing={0} direction="row">
              { campaign.c_chars.filter(o => !o.active).map(c_char => {
                return (
                  <Grid size={4} key={c_char._id}>
                    <CampaignCharacterInput
                      c_char={c_char} 
                      campaign={campaign} 
                      // updateCampaign={() => { setReloading(true); }}
                      onUpdated={() => { setReloading(true); }}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </>
        }
      </Grid>
    ); 
  }
}
